<template>
  <div class="min-h-screen bg-gray-50 dark:bg-neutral-900">
    <Breadcrumb :items="breadcrumbItems" />
    
    <!-- Hero Section -->
    <div class="relative bg-gradient-to-r from-primary to-primary/80 h-[200px] overflow-hidden">
      <div class="absolute inset-0">
        <div class="absolute inset-0 bg-[url('/assets/images/pattern-grid.svg')] opacity-10"></div>
        <div class="absolute inset-0 bg-gradient-to-b from-black/10 to-transparent"></div>
      </div>
      <div class="relative container mx-auto px-4 h-full flex items-center">
        <div class="max-w-2xl text-white">
          <h1 class="text-3xl md:text-4xl font-bold mb-2">{{ t('faq.title') }}</h1>
          <p class="text-lg opacity-90">{{ t('faq.description') }}</p>
        </div>
      </div>
    </div>

    <!-- Content Section -->
    <div class="py-12 mb-10">
      <div class="container mx-auto px-4">
        <!-- Yükleniyor göstergesi -->
        <div v-if="isLoadingCategories || isLoadingFaqs" class="flex justify-center items-center py-20">
          <div class="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary"></div>
        </div>

        <!-- Hata mesajı -->
        <div v-else-if="error" class="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-xl p-6 shadow-sm mb-8">
          <div class="flex items-center">
            <i class="fas fa-exclamation-circle text-xl mr-3"></i>
            <p class="text-lg font-medium">{{ error }}</p>
          </div>
          <button @click="fetchCategories" class="mt-4 px-4 py-2 bg-red-100 dark:bg-red-800/30 hover:bg-red-200 dark:hover:bg-red-800/50 rounded-lg transition-colors">
            {{ t('faq.tryAgain') }}
          </button>
        </div>

        <div v-else>
        <!-- Kategoriler -->
          <div v-if="categories.length > 0" class="flex flex-wrap gap-4 mb-8">
            <!-- Tümü butonu -->
            <button @click="fetchAllFaqs"
                    :class="[
                      'px-6 py-2 rounded-full text-sm font-medium transition-all duration-200',
                      selectedCategory === null 
                        ? 'bg-primary text-white shadow-lg shadow-primary/25'
                        : 'bg-white dark:bg-neutral-800 text-gray-600 dark:text-gray-300 hover:bg-primary/10 dark:hover:bg-primary/10'
                    ]">
              {{ t('faq.allCategories') }}
            </button>
            
            <!-- Kategori butonları -->
          <button v-for="category in categories" 
                  :key="category.id"
                    @click="fetchFaqsByCategory(category.id)"
                  :class="[
                    'px-6 py-2 rounded-full text-sm font-medium transition-all duration-200',
                    selectedCategory === category.id 
                      ? 'bg-primary text-white shadow-lg shadow-primary/25'
                      : 'bg-white dark:bg-neutral-800 text-gray-600 dark:text-gray-300 hover:bg-primary/10 dark:hover:bg-primary/10'
                  ]">
            {{ category.title }}
              <span v-if="category.faq_count" class="ml-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-primary bg-primary/10 rounded-full">
                {{ category.faq_count }}
              </span>
          </button>
        </div>

        <!-- SSS Listesi -->
          <div v-if="faqs.length > 0" class="grid gap-4">
            <!-- Kategori başlığı -->
            <div v-if="categoryDetails" class="mb-6">
              <h2 class="text-xl font-semibold text-gray-900 dark:text-white mb-2">{{ categoryDetails.title }}</h2>
              <p v-if="categoryDetails.description" class="text-gray-600 dark:text-gray-300">{{ categoryDetails.description }}</p>
            </div>
            
            <!-- Sorular -->
            <div v-for="faq in faqs" 
                :key="faq.id"
               class="bg-white dark:bg-neutral-800 rounded-2xl shadow-lg overflow-hidden">
              <button @click="toggleQuestion(faq.id)"
                    class="w-full px-6 py-4 flex items-center justify-between text-left group">
                <span class="text-lg font-medium text-gray-900 dark:text-white">{{ faq.question }}</span>
              <i :class="[
                'fas transition-transform duration-400',
                  activeQuestions.includes(faq.id) ? 'fa-chevron-up rotate-0' : 'fa-chevron-down rotate-180'
              ]" style="transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);"></i>
            </button>
            <transition
              enter-active-class="transition-all duration-400"
              enter-from-class="max-h-0 opacity-0 transform -translate-y-4"
              enter-to-class="max-h-[2000px] opacity-100 transform translate-y-0"
              leave-active-class="transition-all duration-400"
              leave-from-class="max-h-[2000px] opacity-100 transform translate-y-0"
              leave-to-class="max-h-0 opacity-0 transform -translate-y-4">
                <div v-show="activeQuestions.includes(faq.id)"
                   class="px-6 pb-4 overflow-hidden"
                   style="transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);">
                <div class="prose prose-gray dark:prose-invert max-w-none"
                     :style="{
                        transform: activeQuestions.includes(faq.id) ? 'translateY(0)' : 'translateY(-10px)',
                        opacity: activeQuestions.includes(faq.id) ? '1' : '0',
                       transition: 'all 500ms cubic-bezier(0.4, 0, 0.2, 1)'
                      }"
                      v-html="faq.answer">
                  </div>
                </div>
              </transition>
            </div>
          </div>

          <!-- Sonuç yok mesajı -->
          <div v-else-if="!isLoadingFaqs" class="text-center py-12">
            <div class="mx-auto w-24 h-24 mb-4 bg-gray-100 dark:bg-neutral-700 rounded-full flex items-center justify-center">
              <i class="fas fa-question-circle text-3xl text-gray-400 dark:text-gray-500"></i>
              </div>
            <h3 class="text-xl font-medium text-gray-700 dark:text-gray-300 mb-2">{{ t('faq.noQuestions') }}</h3>
            <p class="text-gray-500 dark:text-gray-400 max-w-md mx-auto">{{ t('faq.noQuestionsDesc') }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useFaq } from '~/composables/useFaq'
import { useI18n } from 'vue-i18n'

// i18n
const { t } = useI18n()

// Sayfa meta verisi
definePageMeta({
  title: 'Sık Sorulan Sorular'
})

// Breadcrumb verileri
const breadcrumbItems = [
  { title: t('faq.title'), path: '/faq' }
]

// Sayfa verileri
const pageData = {
  title: t('faq.title'),
  description: t('faq.description')
}

// Aktif sorular (açık akordiyon öğeleri)
const activeQuestions = ref<number[]>([])

// FAQ composable'ını kullan
const { 
  categories, 
  faqs, 
  selectedCategory,
  categoryDetails, 
  isLoadingCategories, 
  isLoadingFaqs, 
  error,
  fetchCategories,
  fetchAllFaqs,
  fetchFaqsByCategory
} = useFaq()

// Soru aç/kapa fonksiyonu
const toggleQuestion = (id: number) => {
  const index = activeQuestions.value.indexOf(id)
  if (index === -1) {
    activeQuestions.value.push(id)
  } else {
    activeQuestions.value.splice(index, 1)
  }
}
</script> 